<template>
  <div class="chat-room">
    <!-- Lista de mensagens -->
    <div class="chat-messages" ref="mensagensContainer">
      <div
        v-for="mensagem in mensagens"
        :key="mensagem.id"
        :class="['mensagem', mensagem.estilo]"
      >
        <div class="message-header" v-if="mensagem.estilo !== 'sistema'">
          <strong>{{ mensagem.nome }}</strong>
          <span class="timestamp">{{ formatarDataHora(mensagem.data) }}</span>
        </div>
        <div class="message-body">{{ mensagem.texto }}</div>
      </div>
    </div>

    <!-- Campo de envio -->
    <div class="chat-input" v-if="podeEnviar">
      <textarea
        ref="inputMensagem"
        v-model="novaMensagem"
        @input="ajustarAltura"
        @keydown.enter.exact
        @keydown.enter.ctrl.prevent="enviarMensagem"
        :placeholder="$t('chat.placeholderMensagem')"
        class="form-control chat-textarea"
        rows="1"
      ></textarea>
      <button @click="enviarMensagem" class="btn btn-send ms-2" title="CTRL+Enter">{{ $t('chat.enviar') }}</button>
    </div>

    <!-- Aviso -->
    <div v-if="!podeEnviar" class="alert alert-info py-2 px-3 mb-2">
      {{ $t('chat.naoFazMaisParte') }}
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import SockJS from 'sockjs-client';
import { Stomp } from '@stomp/stompjs';
import { useUserStore } from '@/utils/stores/userStore';
import { formatarDataHora } from '@/utils/helpers';

export default {
  name: 'ChatRoom',
  props: {
    salaId: Number,
    usuarioId: [Number, String],
    usuarioNome: String
  },
  data() {
    return {
      mensagens: [],
      novaMensagem: '',
      sala: { nome: '' },
      stompClient: null,
      token: '',
      subscription: null,
      podeOuvir: false,
      podeEnviar: false,
      errSub: null,
      wsConectado: false,
    };
  },
  watch: {
    podeOuvir(newVal) {
      if (newVal && this.stompClient?.connected && !this.subscription) {
        this.inscreverSala(this.salaId);
      }
    }
  },
  computed: {
    idUsuario() {
      return parseInt(this.usuarioId);
    },
    salaAtual() {
      return this.sala;
    },
    podeEditar() {
      return this.sala?.assinantes?.length > 2;
    },
    souMembroAtivo() {
      return this.sala?.assinantes?.some(a => a.assinanteURI?.endsWith(`/${this.idUsuario}`))
    }
  },
  async mounted() {
    const userStore = useUserStore();
    this.token = userStore.token;

    await this.carregarSala();      // ← define podeOuvir/podeEnviar
    await this.carregarMensagens(); // (opcional, mas mantém ordem)
    this.conectarWebSocket(); 

    const el = this.$refs.inputMensagem;
    if (el) el.addEventListener('input', this.ajustarAltura);

    this.$watch('salaId', async () => {
      await this.carregarSala();
      await this.carregarMensagens();
      this.reconectarWebSocket();
    });
  },
  beforeUnmount() {
    this.desconectarWebSocket();
    const el = this.$refs.inputMensagem;
    if (el) el.removeEventListener('input', this.ajustarAltura);
  },
  methods: {
    formatarDataHora,

    ehMembro(sala) {
      const id = this.idUsuario;
      const arr = sala?.assinantes || [];
      return arr.some(a => {
        const idField =
          a?.id ??
          a?.assinante?.id ??
          parseInt((a?.assinanteURI || '').split('/').pop()) ??
          parseInt((a?.href || '').split('/').pop());
        return Number(idField) === id;
      });
    },
    async carregarSala() {
      const res = await axios.get(`${this.$dominio}/api/chats/${this.salaId}`, {
        headers: { Authorization: `Bearer ${this.token}` }
      });
      this.sala = res.data;

      const membro = this.ehMembro(this.sala);
      this.podeEnviar = membro;   // habilita somente se REST confirma
      this.podeOuvir  = membro;
      if (!membro && this.subscription) {
        this.subscription.unsubscribe();
        this.subscription = null;
      }
    },
    async carregarMensagens() {
      const res = await axios.get(`${this.$dominio}/api/chats/${this.salaId}/mensagens?page=0&size=100`, {
        headers: { Authorization: `Bearer ${this.token}` }
      });

      this.mensagens = res.data.mensagens.reverse().map(msg => {
        const textoBruto = (msg.texto || '').trim();
        const mQuit = /^__QUIT__\s*(.+)$/.exec(textoBruto);
        const mRemoved = /^__REMOVED__\s*(.+)$/.exec(textoBruto);
        const mAdded   = /^__ADDED__\s*(.+)$/.exec(textoBruto);
        if (mQuit) {
          const nome = mQuit[1].trim() || this.$t('chat.usuarioGenerico');
          return { ...msg, texto: this.$t('chat.saiu', { nome }), estilo: 'sistema', nome: '' };
        }
        if (mRemoved) {
          const nome = mRemoved[1].trim() || this.$t('chat.usuarioGenerico');
          return { ...msg, texto: this.$t('chat.foiRemovido', { nome }), estilo: 'sistema', nome: '' };
        }
        if (mAdded) {
          const nome = mAdded[1].trim() || this.$t('chat.usuarioGenerico');
          return { ...msg, texto: this.$t('chat.foiAdicionado', { nome }), estilo: 'sistema', nome: '' };
        }
        const partes = (msg.remetenteURI || '').trim().split('/');
        const idRemetente = parseInt(partes[partes.length - 1]);
        return { ...msg, estilo: idRemetente === this.idUsuario ? 'enviada' : 'recebida' };
      });

      this.scrollParaBaixo();
    },
    scrollParaBaixo() {
      setTimeout(() => {
        if (this.$refs.mensagensContainer) {
          this.$refs.mensagensContainer.scrollTop = this.$refs.mensagensContainer.scrollHeight;
        }
      }, 50);
    },
    jwtCru() {
      const t = (this.token || '').trim();
      return t.toLowerCase().startsWith('bearer ') ? t.slice(7).trim() : t;
    },
    authHeader() {
      return `Bearer ${this.jwtCru()}`;
    },
    conectarWebSocket() {
      const socket = new SockJS(`${this.$dominio}/chatws`, null, {
        transports: ["xhr-streaming", "xhr-polling"],
      });

      this.stompClient = Stomp.over(() => socket);
      this.stompClient.debug = () => {};

      // marque conectado quando o CONNECT concluir
      this.wsConectado = false;
      this.stompClient.onWebSocketClose = () => { this.wsConectado = false; };

      this.stompClient.onStompError = (frame) => {
        const msg  = (frame?.headers?.message || '').toLowerCase();
        const body = (frame?.body || '').toLowerCase();
        const denied = msg.includes('access denied') || body.includes('forbidden') || body.includes('não é membro');
        if (denied) this.handleExpulsao();
        else console.warn('STOMP error:', frame?.headers?.message, frame?.body);
      };

      this.stompClient.connect(
        { Authorization: this.authHeader() },    // Bearer no CONNECT
        () => {
          this.wsConectado = true;               // <<< IMPORTANTE
          this.subscribeErrors?.();              // se você tiver implementado
          if (this.podeOuvir) this.inscreverSala(this.salaId);
        }
      );
    },
    subscribeErrors() {
      if (this.errSub) this.errSub.unsubscribe();
      this.errSub = this.stompClient.subscribe('/user/queue/errors', (msg) => {
        try {
          const err = JSON.parse(msg.body);
          const txt = (err?.message || '').toLowerCase();
          if (txt.includes('não é membro') || txt.includes('forbidden') || txt.includes('access denied')) {
            this.handleExpulsao();
          }
        } catch {
          const txt = (msg.body || '').toLowerCase();
          if (txt.includes('não é membro') || txt.includes('forbidden') || txt.includes('access denied')) {
            this.handleExpulsao();
          }
        }
      });
    },
    inscreverSala(salaId) {
      if (this.subscription) {
        this.subscription.unsubscribe();
      }

      this.subscription = this.stompClient.subscribe(`/room/${salaId}`, (mensagem) => {
        if (!this.podeOuvir) return; // <<< não processa mais nada se já desligamos

        // --- BLOQUEIO PELO HEADER (robusto por ID) ---
        const getHeader = (name) => {
          const h = mensagem.headers?.[name];
          if (!h) return null;
          // stompjs pode entregar string ou array
          const v = Array.isArray(h) ? h[0] : h;
          return v;
        };
        const parseIdList = (raw) => {
          if (!raw) return [];
          return String(raw)
            .split(',')
            .map(s => parseInt(s.trim(), 10))
            .filter(n => Number.isFinite(n));
        };
        const removedCsv = getHeader('x-removed-users');
        const removedIds = parseIdList(removedCsv);
        if (removedIds.includes(this.idUsuario)) {
          // fui removido nesta ação → desinscreve e bloqueia input imediatamente
          this.handleExpulsao();
        }

        const mensagemRecebida = JSON.parse(mensagem.body);
        const textoBruto = (mensagemRecebida.texto || '').trim();

        const mQuit    = /^__QUIT__\s*(.+)$/.exec(textoBruto);
        const mRemoved = /^__REMOVED__\s*(.+)$/.exec(textoBruto);
        const mAdded   = /^__ADDED__\s*(.+)$/.exec(textoBruto);

        const norm = (s) => (s || '')
          .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
          .toLowerCase().trim();

        if (mQuit || mRemoved) {
          const nome = (mQuit ? mQuit[1] : mRemoved[1]).trim() || this.$t('chat.usuarioGenerico');
          // renderiza a mensagem de sistema
          this.mensagens = [
            ...this.mensagens,
            { ...mensagemRecebida, texto: mQuit ? this.$t('chat.saiu', { nome }) : this.$t('chat.foiRemovido', { nome }), estilo: 'sistema', nome: '' }
          ];

          // >>> Se a mensagem é sobre o PRÓPRIO usuário, encerra assinatura e desativa envio
          const ehMeuNome = norm(this.usuarioNome) === norm(nome);

          if (ehMeuNome) {
            this.handleExpulsao(); // fecha subscribe e desliga flags
          }
        } else if (mAdded) {
          const nome = mAdded[1].trim() || this.$t('chat.usuarioGenerico');

          this.mensagens = [
            ...this.mensagens,
            { ...mensagemRecebida, texto: this.$t('chat.foiAdicionado', { nome }), estilo: 'sistema', nome: '' }
          ]; 
        } else {
          const partes = (mensagemRecebida.remetenteURI || '').trim().split('/');
          const idRemetente = parseInt(partes[partes.length - 1]);
          mensagemRecebida.estilo = idRemetente === this.idUsuario ? 'enviada' : 'recebida';
          this.mensagens = [...this.mensagens, mensagemRecebida];
        }

        // Emitir evento para o ChatApp.vue
        this.$emit('mensagemRecebida', mensagemRecebida);
        this.$nextTick(this.scrollParaBaixo());
        },
        { Authorization: this.authHeader() }
      );
    },
    handleExpulsao() {
      if (this.subscription) {
        this.subscription.unsubscribe();
        this.subscription = null;
      }
      this.podeOuvir = false;
      this.podeEnviar = false;
    },
    reconectarWebSocket() {
      if (this.stompClient?.connected && this.podeOuvir) {
        this.inscreverSala(this.salaId);
      }
    },
    desconectarWebSocket() {
      if (this.subscription) {
        this.subscription.unsubscribe();
        this.subscription = null; // <<< zera
      }
      if (this.errSub) {
        this.errSub.unsubscribe();
        this.errSub = null;
      }
      if (this.stompClient) {
        this.stompClient.disconnect();
      }
    },
    enviarMensagem() {
      if (!this.podeEnviar) return;
      if (!this.stompClient?.connected) {
        console.warn('STOMP ainda não conectado');
        return;
      }
      if (!this.novaMensagem.trim()) return;

      const mensagem = { sala: { id: this.salaId }, texto: this.novaMensagem };

      this.stompClient.send(
        `/chat/${this.salaId}`,
        { Authorization: this.authHeader() },    // Bearer no SEND
        JSON.stringify(mensagem)
      );

      this.novaMensagem = '';

      setTimeout(() => {
        const el = this.$refs.inputMensagem;
        if (el) {
          el.style.height = 'auto';
          el.style.overflowY = 'hidden';
        }
        this.scrollParaBaixo();
      }, 0);
    },
    ajustarAltura() {
      this.$nextTick(() => {
        const el = this.$refs.inputMensagem;
        if (!el) return;

        const MAX = 5 * 24; // 5 linhas x 24px (ajuste p/ seu line-height)
        el.style.height = 'auto';
        const newH = Math.min(el.scrollHeight, MAX);
        el.style.height = newH + 'px';

        // quando passar do limite, mostra scroll
        el.style.overflowY = el.scrollHeight > MAX ? 'auto' : 'hidden';
      });
    },
  }
};
</script>

<style scoped>
.chat-room { 
  display: flex; 
  flex-direction: column; 
  height: 60vh; 
}

.chat-messages { 
  flex: 1;
  padding: 10px; 
  overflow-y: auto; 
  background-color: #fff; 
} 

.mensagem { 
  max-width: 85%; 
  padding: 10px 14px; 
  border-radius: 16px; 
  margin-bottom: 10px; 
  font-size: 15px; 
  word-wrap: break-word; 
  display: block; 
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1); 
} 

.enviada { 
  /* background-color: #d1e7dd; */
  background-color: #f8f9fa;
  align-self: flex-end; 
  text-align: right; 
  margin-left: auto; 
  border-top-right-radius: 0; 
} 

.recebida { 
  background-color: #d1f2eb;
  align-self: flex-start; 
  text-align: left; 
  margin-right: auto; 
  border-top-left-radius: 0; 
} 

.chat-input { 
  display: flex; 
  padding: 12px; 
  border-top: 1px solid #ddd; 
  background-color: #f8f9fa; 
  align-items: center; 
} 

.chat-textarea { 
  resize: none; 
  overflow-y: auto; /* mostra scroll quando passar do max */ 
  min-height: 40px; 
  max-height: 120px; /* ~ 5 linhas */ 
  border-radius: 10px; 
  padding: 10px 15px; 
  font-size: 15px; 
  flex: 1; 
} 

.chat-input .btn { 
  padding: 8px 20px; 
  font-size: 14px; 
  border-radius: 10px; 
  white-space: nowrap; 
  margin-left: 10px; 
} 

.btn-send {
    background-color: var(--sec-color);
    color: var(--main-color);
}

.btn-send:hover {
    background-color: var(--blue-hover);
}

.message-header { 
  font-size: 0.85em; 
  color: #666; 
} 

.message-body { 
  white-space: pre-wrap; /* preserva \n e espaços */ 
  word-break: break-word; /* evita overflow com palavras longas/URLs */ 
} 

.timestamp { 
  margin-left: 5px; 
} /* Centralizar o BALÃO de mensagens de sistema */ 

.mensagem.sistema {
  display: table; 
  margin: 4px auto; 
  max-width: 85%; 
  background: transparent; 
  border: 0; 
  box-shadow: none; 
  padding: 2px 6px; 
} 

.mensagem.sistema .message-body { 
  text-align: center; 
  font-size: 0.9em; 
  line-height: 1.3; 
  color: #666; 
  font-style: bold; 
} 

.mensagem.sistema + .mensagem.sistema { 
  margin-top: 2px; 
}

/* Depois de uma mensagem de SISTEMA, aumente o espaço antes da próxima de USUÁRIO */
.mensagem.sistema + .mensagem.enviada, .mensagem.sistema + .mensagem.recebida { 
  margin-top: 14px; /* ajuste fino: 8–14px conforme o seu gosto */ 
} 

/* Depois de uma mensagem de USUÁRIO, aumente o espaço antes da próxima de SISTEMA */ 
.mensagem.enviada + .mensagem.sistema, .mensagem.recebida + .mensagem.sistema { 
  margin-top: 15px; /* idem — deixa a transição mais “arejada” */ 
}
</style>